import {
  Alert,
  AlertIcon,
  Button,
  Flex,
  Grid,
  GridItem,
  Text,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Input } from '@shared/components';
import { RadioButton } from '@shared/components/RadioButton';
import { DocumentTypeEnum } from '@shared/types/PersonType';
import { links, removeNonNumeric } from '@shared/utils';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import {
  QUERY_KEY_GET_REQUEST_ALL_INFO,
  useCreateRequestCustomer,
  useGetRequestAllInfo,
} from '../../hooks';
import { CheckoutBuyerSchema, ICheckoutBuyerForm } from '../../schemas';
import { ICreateRequestCustomerInput } from '../../services';
import { useBuyerState } from '../../store';

export const DataBuyer = () => {
  const { t } = useTranslation('checkout/data');
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { buyerForm, setBuyerForm } = useBuyerState();

  const { mutate: createRequestCustomer, isLoading } =
    useCreateRequestCustomer();
  const { data: allInfos } = useGetRequestAllInfo();

  const hookForm = useForm<ICheckoutBuyerForm>({
    resolver: zodResolver(CheckoutBuyerSchema()),
    shouldFocusError: false,
    defaultValues: buyerForm,
  });

  const type = hookForm.watch('type');

  const handleSubmit = (data: ICheckoutBuyerForm) => {
    const payload: ICreateRequestCustomerInput = {
      documentType:
        data.type === 'for_company'
          ? DocumentTypeEnum.CNPJ
          : DocumentTypeEnum.CPF,
      email: data.email,
      documentNumber: removeNonNumeric(
        data.type === 'for_company' ? data.cnpj : data.cpf,
      ),
      firstName:
        data.type === 'for_company' ? data.companyName : data.firstName,
      lastName: data.type === 'for_company' ? '.' : data.lastName,
      phoneNumber: removeNonNumeric(data.phone),
    };

    createRequestCustomer(payload, {
      onSuccess: () => {
        setBuyerForm(data);

        queryClient.invalidateQueries(QUERY_KEY_GET_REQUEST_ALL_INFO);

        if (window.location.search.includes('finished')) {
          navigate(links.checkout.summary);
        } else {
          navigate(links.checkout.payment);
        }
      },
    });
  };

  useEffect(() => {
    if (allInfos) {
      const isForCompany =
        allInfos.requestCustomers?.[0]?.documentType === DocumentTypeEnum.CPF;

      if (isForCompany) {
        hookForm.setValue('type', 'for_me');
        hookForm.setValue(
          'cnpj',
          allInfos.requestCustomers?.[0]?.documentNumber,
        );
        hookForm.setValue(
          'firstName',
          allInfos.requestCustomers?.[0]?.firstName,
        );
        hookForm.setValue('lastName', allInfos.requestCustomers?.[0]?.lastName);
      } else {
        hookForm.setValue('type', 'for_company');
        hookForm.setValue(
          'cnpj',
          allInfos.requestCustomers?.[0]?.documentNumber,
        );
        hookForm.setValue(
          'companyName',
          allInfos.requestCustomers?.[0]?.firstName,
        );
      }
      hookForm.setValue('email', allInfos.requestCustomers?.[0]?.email);
      hookForm.setValue('phone', allInfos.requestCustomers?.[0]?.phoneNumber);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allInfos]);

  const typeOptions = [
    { value: 'for_company', label: t('forCompany') },
    { value: 'for_me', label: t('forMe') },
  ];

  return (
    <FormProvider {...hookForm}>
      <form onSubmit={hookForm.handleSubmit(handleSubmit)}>
        <Flex flexDir="column" gap={4}>
          <Text
            color="brand.800"
            fontSize="2xl"
            fontWeight={600}
            textAlign={{ base: 'center', lg: 'start' }}
          >
            {t('subtitle')}
          </Text>

          <Flex
            gap={4}
            h={{ lg: '100dvh' }}
            maxH={{ base: 'calc(100dvh - 186px)', lg: 'calc(100dvh - 214px)' }}
            overflowY="auto"
          >
            <Flex flexDir="column" gap={6} w="full">
              <RadioButton name="type" options={typeOptions} />

              <Grid templateColumns="repeat(2, 1fr)" gap={6} w="full">
                {type === 'for_company' && (
                  <>
                    <GridItem colSpan={{ base: 2, lg: 1 }}>
                      <Input
                        label={t('checkout/data:cnpj')}
                        name="cnpj"
                        placeholder={t('checkout/data:cnpjPlaceholder')}
                        type="cnpj"
                      />
                    </GridItem>

                    <GridItem colSpan={{ base: 2, lg: 1 }}>
                      <Input
                        label={t('checkout/data:companyName')}
                        name="companyName"
                        placeholder={t('checkout/data:companyNamePlaceholder')}
                      />
                    </GridItem>
                  </>
                )}
                {type === 'for_me' && (
                  <>
                    <GridItem colSpan={{ base: 2, lg: 1 }}>
                      <Input
                        label={t('checkout/data:cpf')}
                        name="cpf"
                        type="cpf"
                        placeholder={t('checkout/data:cpfPlaceholder')}
                      />
                    </GridItem>
                    <GridItem colSpan={{ base: 2, lg: 1 }} />
                    <GridItem colSpan={{ base: 2, lg: 1 }}>
                      <Input
                        label={t('checkout/data:name')}
                        name="firstName"
                        placeholder={t('checkout/data:namePlaceholder')}
                      />
                    </GridItem>
                    <GridItem colSpan={{ base: 2, lg: 1 }}>
                      <Input
                        label={t('checkout/data:lastName')}
                        name="lastName"
                        placeholder={t('checkout/data:lastNamePlaceholder')}
                      />
                    </GridItem>
                  </>
                )}

                <GridItem colSpan={2} mt={4}>
                  <Input
                    label={t('checkout/data:email')}
                    name="email"
                    placeholder={t('checkout/data:emailPlaceholder')}
                  />
                </GridItem>

                <GridItem colSpan={2}>
                  <Alert status="info" justifyContent="start">
                    <AlertIcon />
                    {t('checkout/data:emailAlert')}
                  </Alert>
                </GridItem>

                <GridItem colSpan={{ base: 2, lg: 1 }}>
                  <Input
                    label={t('checkout/data:phone')}
                    name="phone"
                    type="phone"
                    placeholder={t('checkout/data:phonePlaceholder')}
                  />
                </GridItem>

                <GridItem colSpan={2}>
                  <Alert status="info" justifyContent="start">
                    <AlertIcon />
                    {t('checkout/data:phoneAlert')}
                  </Alert>
                </GridItem>
              </Grid>
              <Flex
                justifyContent="space-between"
                display={{ base: 'flex', lg: 'none' }}
              >
                <Button
                  as={Link}
                  to={links.checkout.plans}
                  colorScheme="brand"
                  variant="outline"
                >
                  {t('common:previous')}
                </Button>

                <Button
                  colorScheme="brand"
                  w={140}
                  isLoading={hookForm.formState.isSubmitting || isLoading}
                  type="submit"
                >
                  {t('common:continue')}
                </Button>
              </Flex>
            </Flex>
          </Flex>

          <Flex
            justifyContent="space-between"
            display={{ base: 'none', lg: 'flex' }}
          >
            <Button
              as={Link}
              to={links.checkout.plans}
              colorScheme="brand"
              variant="outline"
            >
              {t('common:previous')}
            </Button>

            <Button
              colorScheme="brand"
              w={240}
              isLoading={hookForm.formState.isSubmitting || isLoading}
              type="submit"
            >
              {t('common:continue')}
            </Button>
          </Flex>
        </Flex>
      </form>
    </FormProvider>
  );
};
